import React, { useEffect, useState } from "react";
import AIConfigurationTab from "./AIConfigurationTab";
import CampaignPreferencesTab from "./CampaignPreferencesTab";

/**
 * Google Ads Settings modal - same structure as Meta's Settings modal.
 * Tabs: Connected Accounts; AI Configuration (admin only, shows payloads).
 */
const SettingsModal = ({ isOpen, onClose, isAdmin }) => {
  const rootElement = document.getElementById("react-root");
  const hasGoogleAdsAccounts = rootElement?.getAttribute("has-google-ads-accounts") === "true";
  const [activeTab, setActiveTab] = useState("connect");
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (activeTab === "connect" && hasGoogleAdsAccounts) {
      setLoading(true);
      fetch("/api/google_ads/accounts", { headers: { Accept: "application/json" } })
        .then((res) => (res.ok ? res.json() : { accounts: [] }))
        .then((data) => {
          setAccounts(data.accounts || []);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }
  }, [isOpen, activeTab, hasGoogleAdsAccounts]);

  const openGoogleAdsPopup = () => {
    const url = "/google_ads/oauth/authorize?popup=1";
    const width = 600;
    const height = 700;
    const popupLeft = (window.innerWidth - width) / 2;
    const popupTop = (window.innerHeight - height) / 2;
    const popup = window.open(
      url,
      `googleAdsAuth_${Date.now()}`,
      `popup=yes,width=${width},height=${height},top=${popupTop},left=${popupLeft},resizable=yes,scrollbars=yes,toolbar=0,location=0,menubar=0,status=0`
    );
    if (!popup) {
      alert("Please allow popups in your browser. Redirecting...");
      window.location.href = url;
    }
  };

  const handleRemoveAccount = (account, e) => {
    e.stopPropagation();
    const label = account.name || account.customer_id;
    if (!window.confirm(`Remove "${label}" from connected accounts? You can reconnect later.`)) return;
    const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content");
    fetch(`/api/google_ads/accounts/${account.id}`, {
      method: "DELETE",
      headers: { "X-CSRF-Token": csrf || "", Accept: "application/json" },
    })
      .then(async (res) => {
        const data = res.ok ? null : await res.json().catch(() => ({}));
        if (!res.ok) {
          const msg = data?.error || "Remove failed";
          throw new Error(msg);
        }
        setAccounts((prev) => {
          const next = prev.filter((a) => a.id !== account.id);
          if (next.length === 0) window.location.reload();
          return next;
        });
      })
      .catch((err) => alert(err.message || "Failed to remove account. Please try again."));
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay meta-ads-settings-modal google-ads-settings-modal" onClick={onClose}>
      <div className="modal-container meta-ads-settings-container" onClick={(e) => e.stopPropagation()}>
        <div className="meta-ads-settings-header">
          <h2>Google Ads Settings</h2>
          <button type="button" className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="meta-ads-settings-contents">
          <div className="settings-tabs">
            <button
              type="button"
              className={`settings-tab ${activeTab === "connect" ? "active" : ""}`}
              onClick={() => setActiveTab("connect")}
            >
              Connected Accounts
            </button>
            <button
              type="button"
              className={`settings-tab ${activeTab === "preferences" ? "active" : ""}`}
              onClick={() => setActiveTab("preferences")}
            >
              Campaign Preferences
            </button>
            {isAdmin && (
              <button
                type="button"
                className={`settings-tab ${activeTab === "ai-config" ? "active" : ""}`}
                onClick={() => setActiveTab("ai-config")}
                style={{ flex: "none" }}
              >
                <span style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                  <span>AI Configuration</span>
                  <span className="admin-only-dot" style={{ marginLeft: 0 }} />
                </span>
              </button>
            )}
          </div>

          {activeTab === "connect" && (
            <div className="connect-page-content">
              <div className="google-ads-account-types-info" style={{ marginBottom: 16, padding: "12px 14px", background: "#f0f4f8", borderRadius: 8, fontSize: 13, color: "#374151" }}>
                <strong>Account types:</strong> A <strong>Manager account</strong> (MCC) lets you manage multiple Google Ads accounts in one place. A <strong>Standard account</strong> is where you create and run campaigns. When you connect, we list both; choose a standard account for creating campaigns.
              </div>
              {hasGoogleAdsAccounts ? (
                <div>
                  {loading ? (
                    <p>Loading accounts...</p>
                  ) : accounts.length > 0 ? (
                    <div
                      className="platform-tiles"
                      style={{ gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12 }}
                    >
                      {accounts.map((account) => (
                        <div
                          key={account.id}
                          className="platform-tile connected"
                          style={{
                            position: "relative",
                            minHeight: 110,
                            cursor: "default",
                            padding: "16px",
                            borderRadius: "12px",
                          }}
                        >
                          <button
                            type="button"
                            onClick={(e) => handleRemoveAccount(account, e)}
                            title="Remove account"
                            style={{
                              position: "absolute",
                              top: 8,
                              right: 8,
                              background: "transparent",
                              border: "none",
                              borderRadius: 4,
                              color: "#6b7280",
                              fontSize: 18,
                              lineHeight: 1,
                              width: 28,
                              height: 28,
                              padding: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                            }}
                          >
                            ×
                          </button>
                          <div className="platform-icon" style={{ marginBottom: 8 }}>
                            <svg width="32" height="32" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                            </svg>
                          </div>
                          <h3 style={{ margin: "0 0 4px", fontSize: 16, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#111827" }} title={account.name || account.customer_id}>
                            {account.name || account.customer_id}
                          </h3>
                          <p style={{ margin: 0, fontSize: 12, color: "#6b7280" }}>
                            {account.customer_id} {account.currency ? `• ${account.currency}` : ""}
                            {account.is_manager ? " • Manager account" : " • Standard account"}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p>No accounts found.</p>
                  )}
                  <div style={{ marginTop: 16, display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <button type="button" className="btn btn-primary" onClick={openGoogleAdsPopup}>
                      Connect another account
                    </button>
                  </div>
                </div>
              ) : (
                <div className="connection-prompt">
                  <p>Connect your Google Ads account to create and manage Search campaigns.</p>
                  <button type="button" className="btn btn-primary" onClick={openGoogleAdsPopup}>
                    Connect Google Ads
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === "preferences" && (
            <div className="connect-page-content">
              <CampaignPreferencesTab isAdmin={isAdmin} />
            </div>
          )}

          {activeTab === "ai-config" && isAdmin && (
            <div className="connect-page-content">
              <AIConfigurationTab />
            </div>
          )}

          {isAdmin && (
            <div style={{ padding: "12px 20px", borderTop: "1px solid #e5e7eb" }}>
              <span className="admin-only-legend">
                <span className="admin-only-dot" />
                Admin only
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
